import type { AgentSubscriber } from '@ag-ui/client';
import { HttpAgent } from '@ag-ui/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ChatMessage } from '~/modules/music-iq/pages/chatbot-page/types';

// Music IQ agent (AG-UI) – streaming via /api/music_iq_agent
const MUSIC_IQ_AGENT_URL = '/api/music_iq_agent/';

// Time in ms to wait before showing "thinking" indicator when no chunks arrive.
const THINKING_TIMEOUT_MS = 1000;

export function useMusicIqAgent() {
  const { t } = useTranslation('data', { keyPrefix: 'musicIq' });
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const thinkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // We need two separate states here for tracking while a question is in flight.
  //
  // isThinking can toggle on/off several times while the back end is streaming
  // responses to the question. We set isThinking on a timer because we always
  // want the user to know the back end is "thinking" while waiting for the next
  // set of response streams.
  //
  // isProcessingQuestion toggles only once for the entire question interaction
  // with the back end. So it goes to true when the user asks a question and then
  // is set to false either when the user aborts while waiting or the back end
  // tells us the stream is complete.
  //
  // isProcessingQuestion is used in the <ChatbotPrompter /> to disable all controls
  // while the question is in flight. If we used isThinking, those controls would
  // enable/disable multiple times during the question, which is not what we want.
  const [isThinking, setIsThinking] = useState(false);
  const [isProcessingQuestion, setIsProcessingQuestion] = useState(false);

  const agent = useMemo(
    () =>
      new HttpAgent({
        url: MUSIC_IQ_AGENT_URL,
        threadId: crypto.randomUUID(),
        headers: { Accept: 'text/event-stream' },
      }),
    [],
  );

  // Make sure to clean up the timer on unmount.
  useEffect(() => {
    return () => {
      clearTimeout(thinkingTimeoutRef.current);

      if (agent.isRunning) {
        agent.abortRun();
      }
    };
  }, [agent]);

  const abort = useCallback(() => {
    if (thinkingTimeoutRef.current) {
      clearTimeout(thinkingTimeoutRef.current);
      thinkingTimeoutRef.current = undefined;
    }

    if (agent.isRunning) {
      agent.abortRun();
      setIsThinking(false);
      setIsProcessingQuestion(false);

      setMessages((prev) => [
        ...prev,
        {
          id: `aborted-${Date.now()}`,
          type: 'aborted',
          content: t('chatbot.stoppedResponse'),
        },
      ]);
    }
  }, [t, agent]);

  const removeAgentMessage = useCallback(
    (messageId: string) => {
      agent.setMessages(agent.messages.filter((msg) => msg.id !== messageId));
    },
    [agent],
  );

  const addErrorMessage = useCallback(
    (rawMessage: string) => {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          type: 'error',
          content: t('chatbot.error'),
        },
      ]);

      // We display a generic error message to the user but we want to see the raw
      // message in the console for debugging.
      console.error('Chatbot error:', rawMessage);
    },
    [t],
  );

  const updateMessage = useCallback(
    ({
      targetMessageId,
      textMessageBuffer,
      restartTimer = true,
    }: {
      targetMessageId: string;
      textMessageBuffer: string;
      restartTimer?: boolean;
    }) => {
      setIsThinking(false);

      if (thinkingTimeoutRef.current) {
        clearTimeout(thinkingTimeoutRef.current);
      }

      if (restartTimer) {
        thinkingTimeoutRef.current = setTimeout(() => {
          setIsThinking(true);
        }, THINKING_TIMEOUT_MS);
      }

      // Replace the contents of the agent's response message with the new content.
      setMessages((prev) =>
        prev.map((msg) => {
          return msg.id === targetMessageId
            ? { ...msg, content: textMessageBuffer }
            : msg;
        }),
      );
    },
    [],
  );

  const ask = useCallback(
    async (question: string) => {
      const questionMessageId = `user-${Date.now()}`;
      const responseMessageId = `agent-${Date.now()}`;

      // Add the question as a user message.
      setMessages((prev) => [
        ...prev,
        { id: questionMessageId, type: 'user', content: question },
      ]);

      setIsThinking(true);
      setIsProcessingQuestion(true);

      // Add user message in AG-UI format so the agent sends it in the run.
      agent.addMessage({
        id: questionMessageId,
        role: 'user',
        content: question,
      });

      // Add a placeholder message for the agent's response.
      setMessages((prev) => [
        ...prev,
        { id: responseMessageId, type: 'agent', content: '' },
      ]);

      const subscriber: AgentSubscriber = {
        // -------------------------------------------------------------------------
        // Text Events
        // -------------------------------------------------------------------------
        onTextMessageContentEvent({ textMessageBuffer }) {
          updateMessage({
            targetMessageId: responseMessageId,
            textMessageBuffer,
          });
        },

        onTextMessageEndEvent({ textMessageBuffer }) {
          updateMessage({
            targetMessageId: responseMessageId,
            textMessageBuffer,
            restartTimer: false,
          });
        },

        // -------------------------------------------------------------------------
        // Error Handling
        //
        // We need to handle two events here:
        //   - onRunFailed is called after the agent has received the HTTP request
        //     but BEFORE any stream processing starts.
        //   - onRunErrorEvent is called after stream processing starts.
        //   - They are never called at the same time because they are called from
        //     different paths in the ag-ui code.
        //
        // -------------------------------------------------------------------------
        onRunFailed({ error: runError }) {
          // Ignore AbortError because it was intentionally triggered by the user.
          // Make sure to remove the question so that the agent won't answer it
          // with the next question.
          if (runError.name === 'AbortError') {
            removeAgentMessage(questionMessageId);

            return;
          }

          addErrorMessage(runError.message);
        },

        onRunErrorEvent({ event }) {
          // If it's an abort error, we ignore it because the user intentionally
          // triggered the abort. Make sure to remove the question so that the
          // agent won't answer it with the next question.
          if (event.code === 'abort') {
            removeAgentMessage(questionMessageId);

            return;
          }

          const errorMessage = `code[${event.code}] message[${event.message ?? 'unknown error'}]`;

          addErrorMessage(errorMessage);
        },
      };

      try {
        await agent.runAgent({}, subscriber);
      } catch {
        // NOTE: Debugged this in the ag-ui code itself and even if we handle the
        //       error in onRunFailed(), ag-ui still re-throws the error. So catch
        //       here to swallow the error. Specifically don't add an error message
        //       here or we'd end up with duplicate error messages.
      } finally {
        if (thinkingTimeoutRef.current) {
          clearTimeout(thinkingTimeoutRef.current);
          thinkingTimeoutRef.current = undefined;
        }

        setIsThinking(false);
        setIsProcessingQuestion(false);
      }
    },
    [agent, addErrorMessage, removeAgentMessage, updateMessage],
  );

  return { messages, ask, abort, isThinking, isProcessingQuestion };
}
